import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Calculator,
  Check,
  Layers,
  MousePointerClick,
  Plus,
  Replace,
  Trash2,
  Undo2,
  Upload,
  Variable,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  FeedbackState,
  SearchInput,
  Tabs,
  type BadgeProps,
} from "@h2j-data-team/ui";

import {
  buildExpressionGraph,
  createGroupVariableInputSchema,
  createGroupDimensionInputSchema,
  createGroupMeasureInputSchema,
  updateGroupVariableInputSchema,
  updateGroupDimensionInputSchema,
  updateGroupMeasureInputSchema,
  type CreateGroupVariableInput,
  type CreateGroupDimensionInput,
  type CreateGroupMeasureInput,
  type UpdateGroupVariableInput,
  type UpdateGroupDimensionInput,
  type UpdateGroupMeasureInput,
  type GroupApp,
  type GroupVariableWithSync,
  type GroupDimensionWithSync,
  type GroupMeasureWithSync,
  type GraphItemInput,
  type GraphItemKind,
  type QlikField,
  type SyncStatus,
} from "@repo/shared";
import { DETAIL_GRID, ExpressionDecomposition } from "@/components/qlik/expression-decomposition";
import {
  FindReplaceBar,
  type FindReplaceItem,
  type FindReplacement,
} from "@/components/qlik/find-replace-bar";
import { RowTags, TagFilter } from "@/components/qlik/item-tags";
import { ConfirmDialog, useConfirmDialog, toast } from "@/components/shared";
import { ResizeHandle } from "@/components/resize-handle";
import { getErrorMessage } from "@/lib/api-client";
import { useResizablePanelWidth } from "@/lib/use-resizable-panel-width";
import { cn } from "@/lib/utils";
import {
  useCreateGroupVariable,
  useUpdateGroupVariable,
  useDeleteGroupVariable,
  useCreateGroupDimension,
  useUpdateGroupDimension,
  useDeleteGroupDimension,
  useCreateGroupMeasure,
  useUpdateGroupMeasure,
  useDeleteGroupMeasure,
} from "@/features/groups/api";
import {
  useGroupItemsDraft,
  isNewId,
  newTempId,
  variableToForm,
  dimensionToForm,
  measureToForm,
  emptyVariableForm,
  emptyDimensionForm,
  emptyMeasureForm,
  type GroupItemKind,
  type VariableForm,
  type DimensionForm,
  type MeasureForm,
} from "@/features/groups/use-group-items-draft";
import {
  GroupVariableForm,
  GroupDimensionForm,
  GroupMeasureForm,
  GroupItemSyncStatus,
  type AppSyncEntry,
} from "./group-item-forms";
import { statusKey } from "../sync-status";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type RowState = "deleted" | "unsaved" | "edited" | "new" | "clean";

interface EditorRow {
  id: string;
  name: string;
  secondary: string;
  tags: string[];
  status: SyncStatus | null;
  state: RowState;
  isNew: boolean;
}

interface ScratchState {
  variables: Map<string, VariableForm>;
  dimensions: Map<string, DimensionForm>;
  measures: Map<string, MeasureForm>;
}

const SYNC_DOT: Record<SyncStatus, string> = {
  in_sync: "bg-status-success-foreground",
  out_of_sync: "bg-status-warning-foreground",
  missing: "bg-status-danger-foreground",
  unknown: "bg-border",
};

function worstStatus(statuses: { status: SyncStatus }[]): SyncStatus | null {
  if (statuses.length === 0) return null;
  if (statuses.some((s) => s.status === "missing")) return "missing";
  if (statuses.some((s) => s.status === "out_of_sync")) return "out_of_sync";
  if (statuses.some((s) => s.status === "unknown")) return "unknown";
  return "in_sync";
}

function compareAlphabetically(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function zodToErrors(error: { issues: ReadonlyArray<{ path: PropertyKey[]; message: string }> }) {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    if (typeof key === "string" && !out[key]) out[key] = issue.message;
  }
  return out;
}

function splitTags(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// Form -> API payload builders
function buildVarCreate(f: VariableForm): CreateGroupVariableInput {
  return {
    name: f.name.trim(),
    definition: f.definition.trim(),
    description: f.description.trim() || undefined,
  };
}
function buildVarUpdate(f: VariableForm): UpdateGroupVariableInput {
  return {
    name: f.name.trim(),
    definition: f.definition.trim(),
    description: f.description.trim() ? f.description.trim() : null,
  };
}
function dimShared(f: DimensionForm) {
  const valid = f.fields.filter((x) => x.def.trim());
  const fieldDefs = valid.map((x) => x.def.trim());
  const fieldLabels = f.grouping !== "N" ? valid.map((x) => x.label.trim()) : [];
  const tags = splitTags(f.tagsText);
  return {
    title: f.title.trim(),
    fieldDefs,
    fieldLabels,
    grouping: f.grouping,
    tags,
    labelExpression: f.grouping === "N" ? f.labelExpression.trim() : "",
  };
}
function buildDimCreate(f: DimensionForm): CreateGroupDimensionInput {
  return { ...dimShared(f), description: f.description.trim() || undefined };
}
function buildDimUpdate(f: DimensionForm): UpdateGroupDimensionInput {
  return { ...dimShared(f), description: f.description.trim() ? f.description.trim() : null };
}
function measureShared(f: MeasureForm) {
  return {
    title: f.title.trim(),
    expression: f.expression.trim(),
    tags: splitTags(f.tagsText),
  };
}
function buildMeasureCreate(f: MeasureForm): CreateGroupMeasureInput {
  return {
    ...measureShared(f),
    description: f.description.trim() || undefined,
    label: f.label.trim() || undefined,
    numberFormat: f.numberFormat.trim() || undefined,
    color: f.color.trim() || undefined,
  };
}
function buildMeasureUpdate(f: MeasureForm): UpdateGroupMeasureInput {
  return {
    ...measureShared(f),
    description: f.description.trim() ? f.description.trim() : null,
    label: f.label.trim() ? f.label.trim() : null,
    numberFormat: f.numberFormat.trim() ? f.numberFormat.trim() : null,
    color: f.color.trim() ? f.color.trim() : null,
  };
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

export function GroupItemsEditor({
  groupId,
  variables,
  dimensions,
  measures,
  apps,
  availableFields,
  onImport,
  initialTab,
  initialItem,
  onSelectionChange,
}: {
  groupId: string;
  variables: GroupVariableWithSync[];
  dimensions: GroupDimensionWithSync[];
  measures: GroupMeasureWithSync[];
  apps: GroupApp[];
  availableFields: QlikField[] | undefined;
  onImport: () => void;
  initialTab?: GroupItemKind;
  initialItem?: string;
  onSelectionChange?: (tab: GroupItemKind, item: string | null) => void;
}) {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const draft = useGroupItemsDraft();
  const { confirm, dialogProps } = useConfirmDialog();
  const { width: masterWidth, startResize, tabsRef, resizeProps } = useResizablePanelWidth();
  const [scratch, setScratch] = useState<ScratchState>({
    variables: new Map(),
    dimensions: new Map(),
    measures: new Map(),
  });
  const [activeTab, setActiveTab] = useState<GroupItemKind>(initialTab ?? "dimensions");
  const [selectedId, setSelectedId] = useState<string | null>(initialItem ?? null);
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Mirror selection into the URL from the user-action handlers (not an effect): an
  // effect fires on mount / StrictMode / route transitions and races with navigating
  // away. Handlers fire only on real clicks — like the search inputs do.
  const handleSelect = (id: string | null) => {
    setSelectedId(id);
    onSelectionChange?.(activeTab, id);
  };
  const handleTabChange = (tab: GroupItemKind) => {
    setActiveTab(tab);
    setSelectedId(null);
    setSelectedTags([]);
    onSelectionChange?.(tab, null);
  };
  const detailPanelRef = useRef<HTMLDivElement>(null);
  // "Open" from the decomposition: switch tab and item together (handleTabChange clears the selection).
  const openItem = (kind: GraphItemKind, id: string) => {
    const tab: GroupItemKind =
      kind === "measure" ? "measures" : kind === "dimension" ? "dimensions" : "variables";
    setActiveTab(tab);
    setSelectedId(id);
    setSelectedTags([]);
    onSelectionChange?.(tab, id);
    detailPanelRef.current?.scrollTo({ top: 0 });
  };
  const [errorsByKey, setErrorsByKey] = useState<Record<string, Record<string, string>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [findShowReplace, setFindShowReplace] = useState(true);

  const createVariable = useCreateGroupVariable();
  const updateVariable = useUpdateGroupVariable();
  const deleteVariable = useDeleteGroupVariable();
  const createDimension = useCreateGroupDimension();
  const updateDimension = useUpdateGroupDimension();
  const deleteDimension = useDeleteGroupDimension();
  const createMeasure = useCreateGroupMeasure();
  const updateMeasure = useUpdateGroupMeasure();
  const deleteMeasure = useDeleteGroupMeasure();

  const sortedVariables = useMemo(
    () => [...variables].sort((a, b) => compareAlphabetically(a.name, b.name)),
    [variables],
  );
  const sortedDimensions = useMemo(
    () => [...dimensions].sort((a, b) => compareAlphabetically(a.title, b.title)),
    [dimensions],
  );
  const sortedMeasures = useMemo(
    () => [...measures].sort((a, b) => compareAlphabetically(a.title, b.title)),
    [measures],
  );

  const varById = useMemo(() => new Map(variables.map((v) => [v.id, v])), [variables]);
  const dimById = useMemo(() => new Map(dimensions.map((d) => [d.id, d])), [dimensions]);
  const measureById = useMemo(() => new Map(measures.map((m) => [m.id, m])), [measures]);

  // ---- Scratch (local, unsaved working forms) ----
  const setScratchVar = (id: string, f: VariableForm) =>
    setScratch((p) => ({ ...p, variables: new Map(p.variables).set(id, f) }));
  const setScratchDim = (id: string, f: DimensionForm) =>
    setScratch((p) => ({ ...p, dimensions: new Map(p.dimensions).set(id, f) }));
  const setScratchMeasure = (id: string, f: MeasureForm) =>
    setScratch((p) => ({ ...p, measures: new Map(p.measures).set(id, f) }));
  const clearScratch = (kind: GroupItemKind, id: string) =>
    setScratch((p) => {
      if (kind === "variables") {
        const m = new Map(p.variables);
        m.delete(id);
        return { ...p, variables: m };
      }
      if (kind === "dimensions") {
        const m = new Map(p.dimensions);
        m.delete(id);
        return { ...p, dimensions: m };
      }
      const m = new Map(p.measures);
      m.delete(id);
      return { ...p, measures: m };
    });

  // ---- Effective ("working") forms: scratch > staged draft > persisted ----
  const workingVar = (id: string): VariableForm =>
    scratch.variables.get(id) ??
    (isNewId(id)
      ? (draft.variables.creates.get(id) ?? emptyVariableForm())
      : (draft.variables.edits.get(id) ??
        (varById.has(id) ? variableToForm(varById.get(id)!) : emptyVariableForm())));
  const workingDim = (id: string): DimensionForm =>
    scratch.dimensions.get(id) ??
    (isNewId(id)
      ? (draft.dimensions.creates.get(id) ?? emptyDimensionForm())
      : (draft.dimensions.edits.get(id) ??
        (dimById.has(id) ? dimensionToForm(dimById.get(id)!) : emptyDimensionForm())));
  const workingMeasure = (id: string): MeasureForm =>
    scratch.measures.get(id) ??
    (isNewId(id)
      ? (draft.measures.creates.get(id) ?? emptyMeasureForm())
      : (draft.measures.edits.get(id) ??
        (measureById.has(id) ? measureToForm(measureById.get(id)!) : emptyMeasureForm())));

  // ---- New-item ids (transient scratch creates ∪ staged creates) ----
  const newVarIds = useMemo(
    () => [
      ...new Set([...scratch.variables.keys(), ...draft.variables.creates.keys()].filter(isNewId)),
    ],
    [scratch.variables, draft.variables.creates],
  );
  const newDimIds = useMemo(
    () => [
      ...new Set(
        [...scratch.dimensions.keys(), ...draft.dimensions.creates.keys()].filter(isNewId),
      ),
    ],
    [scratch.dimensions, draft.dimensions.creates],
  );
  const newMeasureIds = useMemo(
    () => [
      ...new Set([...scratch.measures.keys(), ...draft.measures.creates.keys()].filter(isNewId)),
    ],
    [scratch.measures, draft.measures.creates],
  );

  // ---- Expression graph for the decomposition: the group's working items only ----
  const expressionGraph = useMemo(() => {
    const items: GraphItemInput[] = [];
    for (const id of [...variables.map((v) => v.id), ...newVarIds]) {
      if (draft.variables.deletes.has(id)) continue;
      const f = workingVar(id);
      items.push({
        kind: "variable",
        id,
        name: f.name || (varById.get(id)?.name ?? ""),
        sources: [{ id: "expression", text: f.definition }],
      });
    }
    for (const id of [...dimensions.map((d) => d.id), ...newDimIds]) {
      if (draft.dimensions.deletes.has(id)) continue;
      const f = workingDim(id);
      items.push({
        kind: "dimension",
        id,
        name: f.title || (dimById.get(id)?.title ?? ""),
        sources: [
          ...f.fields.map((x, i) => ({ id: `field:${i}` as const, text: x.def })),
          { id: "label", text: f.labelExpression },
        ],
      });
    }
    for (const id of [...measures.map((m) => m.id), ...newMeasureIds]) {
      if (draft.measures.deletes.has(id)) continue;
      const f = workingMeasure(id);
      items.push({
        kind: "measure",
        id,
        name: f.title || (measureById.get(id)?.title ?? ""),
        sources: [
          { id: "expression", text: f.expression },
          { id: "label", text: f.label },
        ],
      });
    }
    // A $(name) the group doesn't hold is managed per app, not missing.
    return buildExpressionGraph(items, { unresolved: "outside" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables, dimensions, measures, scratch, draft, newVarIds, newDimIds, newMeasureIds]);

  // ---- Master list rows for the active tab ----
  const rows = useMemo<EditorRow[]>(() => {
    const out: EditorRow[] = [];
    if (activeTab === "variables") {
      for (const v of sortedVariables) {
        const f = workingVar(v.id);
        out.push({
          id: v.id,
          name: f.name || v.name,
          secondary: f.definition,
          tags: [],
          status: worstStatus(v.syncStatus),
          state: draft.variables.deletes.has(v.id)
            ? "deleted"
            : scratch.variables.has(v.id)
              ? "unsaved"
              : draft.variables.edits.has(v.id)
                ? "edited"
                : "clean",
          isNew: false,
        });
      }
      for (const id of newVarIds) {
        const f = workingVar(id);
        out.push({
          id,
          name: f.name,
          secondary: f.definition,
          tags: [],
          status: null,
          state: scratch.variables.has(id)
            ? "unsaved"
            : draft.variables.creates.has(id)
              ? "new"
              : "clean",
          isNew: true,
        });
      }
    } else if (activeTab === "dimensions") {
      for (const d of sortedDimensions) {
        const f = workingDim(d.id);
        out.push({
          id: d.id,
          name: f.title || d.title,
          secondary: f.fields
            .map((x) => x.def)
            .filter(Boolean)
            .join(", "),
          tags: splitTags(f.tagsText),
          status: worstStatus(d.syncStatus),
          state: draft.dimensions.deletes.has(d.id)
            ? "deleted"
            : scratch.dimensions.has(d.id)
              ? "unsaved"
              : draft.dimensions.edits.has(d.id)
                ? "edited"
                : "clean",
          isNew: false,
        });
      }
      for (const id of newDimIds) {
        const f = workingDim(id);
        out.push({
          id,
          name: f.title,
          secondary: f.fields
            .map((x) => x.def)
            .filter(Boolean)
            .join(", "),
          tags: splitTags(f.tagsText),
          status: null,
          state: scratch.dimensions.has(id)
            ? "unsaved"
            : draft.dimensions.creates.has(id)
              ? "new"
              : "clean",
          isNew: true,
        });
      }
    } else {
      for (const m of sortedMeasures) {
        const f = workingMeasure(m.id);
        out.push({
          id: m.id,
          name: f.title || m.title,
          secondary: f.expression,
          tags: splitTags(f.tagsText),
          status: worstStatus(m.syncStatus),
          state: draft.measures.deletes.has(m.id)
            ? "deleted"
            : scratch.measures.has(m.id)
              ? "unsaved"
              : draft.measures.edits.has(m.id)
                ? "edited"
                : "clean",
          isNew: false,
        });
      }
      for (const id of newMeasureIds) {
        const f = workingMeasure(id);
        out.push({
          id,
          name: f.title,
          secondary: f.expression,
          tags: splitTags(f.tagsText),
          status: null,
          state: scratch.measures.has(id)
            ? "unsaved"
            : draft.measures.creates.has(id)
              ? "new"
              : "clean",
          isNew: true,
        });
      }
    }
    out.sort((a, b) => compareAlphabetically(a.name, b.name));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    sortedVariables,
    sortedDimensions,
    sortedMeasures,
    scratch,
    draft,
    newVarIds,
    newDimIds,
    newMeasureIds,
  ]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (selectedTags.length === 0 || r.tags.some((tag) => selectedTags.includes(tag))) &&
        (!q || r.name.toLowerCase().includes(q) || r.secondary.toLowerCase().includes(q)),
    );
  }, [rows, search, selectedTags]);
  const tagLists = useMemo(() => rows.map((r) => r.tags), [rows]);

  const counts = {
    variables: sortedVariables.length + newVarIds.length,
    dimensions: sortedDimensions.length + newDimIds.length,
    measures: sortedMeasures.length + newMeasureIds.length,
  };

  const scratchCount = scratch.variables.size + scratch.dimensions.size + scratch.measures.size;

  // ---- Find & Replace (explicit bulk action → stages to the draft) ----
  const findItems = useMemo<FindReplaceItem[]>(() => {
    return rows.map((r) => {
      if (activeTab === "variables") {
        const f = workingVar(r.id);
        return { id: r.id, name: f.name, content: f.definition };
      }
      if (activeTab === "dimensions") {
        const f = workingDim(r.id);
        return { id: r.id, name: f.title, content: f.fields.map((x) => x.def).join("\n") };
      }
      const f = workingMeasure(r.id);
      return { id: r.id, name: f.title, content: f.expression };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, activeTab]);

  const applyReplacements = (reps: FindReplacement[]) => {
    const byId = new Map<string, FindReplacement[]>();
    for (const r of reps) {
      const list = byId.get(r.itemId);
      if (list) list.push(r);
      else byId.set(r.itemId, [r]);
    }
    for (const [id, rs] of byId) {
      if (activeTab === "variables") {
        let f = workingVar(id);
        for (const r of rs)
          f = r.field === "name" ? { ...f, name: r.newValue } : { ...f, definition: r.newValue };
        if (isNewId(id)) draft.variables.setCreate(id, f);
        else draft.variables.setEdit(id, f);
        clearScratch("variables", id);
      } else if (activeTab === "dimensions") {
        let f = workingDim(id);
        for (const r of rs) {
          if (r.field === "name") f = { ...f, title: r.newValue };
          else {
            const defs = r.newValue.split("\n").filter(Boolean);
            f = {
              ...f,
              fields:
                defs.length > 0
                  ? defs.map((def, i) => ({ def, label: f.fields[i]?.label ?? "" }))
                  : [{ def: "", label: "" }],
            };
          }
        }
        if (isNewId(id)) draft.dimensions.setCreate(id, f);
        else draft.dimensions.setEdit(id, f);
        clearScratch("dimensions", id);
      } else {
        let f = workingMeasure(id);
        for (const r of rs)
          f = r.field === "name" ? { ...f, title: r.newValue } : { ...f, expression: r.newValue };
        if (isNewId(id)) draft.measures.setCreate(id, f);
        else draft.measures.setEdit(id, f);
        clearScratch("measures", id);
      }
    }
  };

  // ---- Item actions ----
  const handleNew = () => {
    const tempId = newTempId();
    if (activeTab === "variables") setScratchVar(tempId, emptyVariableForm());
    else if (activeTab === "dimensions") setScratchDim(tempId, emptyDimensionForm());
    else setScratchMeasure(tempId, emptyMeasureForm());
    setSelectedId(tempId);
  };

  const clearItemError = (id: string) =>
    setErrorsByKey((p) => {
      if (!p[id]) return p;
      const n = { ...p };
      delete n[id];
      return n;
    });

  const handleSaveItem = () => {
    const id = selectedId;
    if (id == null) return;
    const isNew = isNewId(id);
    if (activeTab === "variables") {
      const f = workingVar(id);
      const res = isNew
        ? createGroupVariableInputSchema.safeParse(buildVarCreate(f))
        : updateGroupVariableInputSchema.safeParse(buildVarUpdate(f));
      if (!res.success) {
        setErrorsByKey((p) => ({ ...p, [id]: zodToErrors(res.error) }));
        return;
      }
      if (isNew) draft.variables.setCreate(id, f);
      else draft.variables.setEdit(id, f);
      clearScratch("variables", id);
    } else if (activeTab === "dimensions") {
      const f = workingDim(id);
      const res = isNew
        ? createGroupDimensionInputSchema.safeParse(buildDimCreate(f))
        : updateGroupDimensionInputSchema.safeParse(buildDimUpdate(f));
      if (!res.success) {
        setErrorsByKey((p) => ({ ...p, [id]: zodToErrors(res.error) }));
        return;
      }
      if (isNew) draft.dimensions.setCreate(id, f);
      else draft.dimensions.setEdit(id, f);
      clearScratch("dimensions", id);
    } else {
      const f = workingMeasure(id);
      const res = isNew
        ? createGroupMeasureInputSchema.safeParse(buildMeasureCreate(f))
        : updateGroupMeasureInputSchema.safeParse(buildMeasureUpdate(f));
      if (!res.success) {
        setErrorsByKey((p) => ({ ...p, [id]: zodToErrors(res.error) }));
        return;
      }
      if (isNew) draft.measures.setCreate(id, f);
      else draft.measures.setEdit(id, f);
      clearScratch("measures", id);
    }
    clearItemError(id);
  };

  const handleDiscardItem = () => {
    const id = selectedId;
    if (id == null) return;
    const isNew = isNewId(id);
    clearScratch(activeTab, id);
    if (activeTab === "variables") {
      if (isNew) draft.variables.removeCreate(id);
      else draft.variables.removeEdit(id);
    } else if (activeTab === "dimensions") {
      if (isNew) draft.dimensions.removeCreate(id);
      else draft.dimensions.removeEdit(id);
    } else {
      if (isNew) draft.measures.removeCreate(id);
      else draft.measures.removeEdit(id);
    }
    clearItemError(id);
    if (isNew) setSelectedId(null);
  };

  const handleDelete = () => {
    const id = selectedId;
    if (id == null) return;
    clearScratch(activeTab, id);
    if (activeTab === "variables") draft.variables.addDelete(id);
    else if (activeTab === "dimensions") draft.dimensions.addDelete(id);
    else draft.measures.addDelete(id);
    clearItemError(id);
  };

  const handleUndoDelete = () => {
    const id = selectedId;
    if (id == null) return;
    if (activeTab === "variables") draft.variables.removeDelete(id);
    else if (activeTab === "dimensions") draft.dimensions.removeDelete(id);
    else draft.measures.removeDelete(id);
  };

  const handleDiscardAll = () => {
    confirm({
      title: t("editor.discardAllTitle"),
      description: t("editor.discardAllDescription", { count: draft.pendingCount }),
      confirmLabel: t("editor.discardAllConfirm"),
      variant: "destructive",
      onConfirm: async () => {
        draft.clearAll();
        setScratch({ variables: new Map(), dimensions: new Map(), measures: new Map() });
        setSelectedId(null);
        setErrorsByKey({});
      },
    });
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      for (const [id, f] of draft.variables.creates) {
        await createVariable.mutateAsync({ groupId, data: buildVarCreate(f) });
        draft.variables.removeCreate(id);
      }
      for (const [id, f] of draft.variables.edits) {
        if (draft.variables.deletes.has(id)) continue;
        await updateVariable.mutateAsync({ groupId, variableId: id, data: buildVarUpdate(f) });
        draft.variables.removeEdit(id);
      }
      for (const id of draft.variables.deletes) {
        await deleteVariable.mutateAsync({ groupId, variableId: id });
        draft.variables.removeDelete(id);
      }
      for (const [id, f] of draft.dimensions.creates) {
        await createDimension.mutateAsync({ groupId, data: buildDimCreate(f) });
        draft.dimensions.removeCreate(id);
      }
      for (const [id, f] of draft.dimensions.edits) {
        if (draft.dimensions.deletes.has(id)) continue;
        await updateDimension.mutateAsync({ groupId, dimensionId: id, data: buildDimUpdate(f) });
        draft.dimensions.removeEdit(id);
      }
      for (const id of draft.dimensions.deletes) {
        await deleteDimension.mutateAsync({ groupId, dimensionId: id });
        draft.dimensions.removeDelete(id);
      }
      for (const [id, f] of draft.measures.creates) {
        await createMeasure.mutateAsync({ groupId, data: buildMeasureCreate(f) });
        draft.measures.removeCreate(id);
      }
      for (const [id, f] of draft.measures.edits) {
        if (draft.measures.deletes.has(id)) continue;
        await updateMeasure.mutateAsync({ groupId, measureId: id, data: buildMeasureUpdate(f) });
        draft.measures.removeEdit(id);
      }
      for (const id of draft.measures.deletes) {
        await deleteMeasure.mutateAsync({ groupId, measureId: id });
        draft.measures.removeDelete(id);
      }
      toast.success(t("editor.savedAll"));
      setSelectedId(null);
    } catch (err) {
      toast.error(getErrorMessage(err, t("editor.saveFailed")));
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Selected item state ----
  const sel = selectedId;
  const activeScratch =
    activeTab === "variables"
      ? scratch.variables
      : activeTab === "dimensions"
        ? scratch.dimensions
        : scratch.measures;
  const activeDraft =
    activeTab === "variables"
      ? draft.variables
      : activeTab === "dimensions"
        ? draft.dimensions
        : draft.measures;

  const selNew = sel != null && isNewId(sel);
  const selDirty = sel != null && activeScratch.has(sel);
  const selDeleted = sel != null && !selNew && activeDraft.deletes.has(sel);
  const selStaged =
    sel != null && (selNew ? activeDraft.creates.has(sel) : activeDraft.edits.has(sel));

  const tabKey =
    activeTab === "variables" ? "Variable" : activeTab === "dimensions" ? "Dimension" : "Measure";
  const eyebrow = selNew ? t(`editor.new${tabKey}`) : t(`editor.editing${tabKey}`);

  const appsByGroupAppId = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);

  const syncEntries = useMemo<AppSyncEntry[]>(() => {
    if (sel == null || isNewId(sel)) return [];
    const meta = (groupAppId: string) => {
      const app = appsByGroupAppId.get(groupAppId);
      return { connectionName: app?.connectionName, spaceName: app?.spaceName };
    };
    if (activeTab === "variables") {
      return (
        varById.get(sel)?.syncStatus.map((s) => ({
          groupAppId: s.groupAppId,
          appName: s.appName,
          ...meta(s.groupAppId),
          status: s.status,
          remote: s.remoteDefinition,
        })) ?? []
      );
    }
    if (activeTab === "dimensions") {
      return (
        dimById.get(sel)?.syncStatus.map((s) => ({
          groupAppId: s.groupAppId,
          appName: s.appName,
          ...meta(s.groupAppId),
          status: s.status,
          remote: s.remoteFieldDefs ? s.remoteFieldDefs.join(", ") : null,
        })) ?? []
      );
    }
    return (
      measureById.get(sel)?.syncStatus.map((s) => ({
        groupAppId: s.groupAppId,
        appName: s.appName,
        ...meta(s.groupAppId),
        status: s.status,
        remote: s.remoteExpression,
      })) ?? []
    );
  }, [sel, activeTab, varById, dimById, measureById, appsByGroupAppId]);

  const EmptyIcon =
    activeTab === "variables" ? Variable : activeTab === "dimensions" ? Layers : Calculator;

  const rowBadge = (state: RowState): { label: string; variant: BadgeProps["variant"] } | null => {
    if (state === "deleted") return { label: t("editor.badgeDeleting"), variant: "danger" };
    if (state === "unsaved") return { label: t("editor.badgeUnsaved"), variant: "neutral" };
    if (state === "new") return { label: t("editor.badgeNew"), variant: "info" };
    if (state === "edited") return { label: t("editor.badgeEdited"), variant: "warning" };
    return null;
  };
  const detailBadge = selDeleted
    ? rowBadge("deleted")
    : selDirty
      ? rowBadge("unsaved")
      : selNew && selStaged
        ? rowBadge("new")
        : selStaged
          ? rowBadge("edited")
          : null;

  // The tab row sets the master panel's minimum width (the drag cannot cut the labels), so
  // the width hook measures the library's tablist rather than a row of its own.
  const tabsRootRef = useCallback(
    (root: HTMLDivElement | null) => {
      const list = root?.querySelector<HTMLElement>('[role="tablist"]');
      return list ? tabsRef(list) : undefined;
    },
    [tabsRef],
  );

  const masterPane = (
    <>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <SearchInput
          size="compact"
          containerClassName="flex-1"
          value={search}
          onValueChange={setSearch}
          clearLabel={tc("list.clearSearch")}
          placeholder={t("editor.searchPlaceholder")}
          aria-label={t("editor.searchPlaceholder")}
        />
        <Button
          variant="outline"
          size="icon-compact"
          className="shrink-0"
          onClick={handleNew}
          aria-label={t("editor.new" + tabKey)}
          title={t("editor.new" + tabKey)}
        >
          <Plus />
        </Button>
      </div>
      <div className="px-3 pb-2.5 empty:hidden">
        <TagFilter tagLists={tagLists} value={selectedTags} onValueChange={setSelectedTags} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {filteredRows.length === 0 ? (
          <FeedbackState
            state={search.trim() || selectedTags.length ? "no-results" : "empty"}
            icon={<EmptyIcon />}
            title={
              search.trim() || selectedTags.length ? t("editor.noMatch") : t("editor.emptyList")
            }
          />
        ) : (
          <ul>
            {filteredRows.map((row) => {
              const badge = rowBadge(row.state);
              const selected = selectedId === row.id;
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    aria-current={selected ? "true" : undefined}
                    onClick={() => handleSelect(row.id)}
                    className={cn(
                      "flex w-full items-start gap-2 px-3 py-2.5 text-left hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                      selected && "bg-muted",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "mt-1.5 size-1.5 shrink-0 rounded-full",
                        row.state === "deleted"
                          ? "bg-status-danger-foreground"
                          : row.isNew
                            ? "bg-brand"
                            : row.status
                              ? SYNC_DOT[row.status]
                              : "bg-border",
                      )}
                    />
                    {row.status && (
                      <span className="sr-only">{t(`syncStatus.${statusKey(row.status)}`)}</span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          "block truncate text-[13px] font-medium leading-tight",
                          row.state === "deleted" ? "text-faint line-through" : "text-ink",
                        )}
                      >
                        {row.name || (
                          <span className="italic text-faint">{t("editor.untitled")}</span>
                        )}
                      </span>
                      {row.secondary && (
                        <span className="mt-0.5 block truncate font-mono text-[11px] text-faint">
                          {row.secondary}
                        </span>
                      )}
                      <RowTags tags={row.tags} selected={selectedTags} />
                    </span>
                    {badge && (
                      <Badge variant={badge.variant} className="shrink-0 font-mono">
                        {badge.label}
                      </Badge>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );

  const tabLabel = (label: string, count: number) => (
    <span className="inline-flex items-center gap-1.5">
      {label}
      <span className="font-mono text-[10px] text-faint">{count}</span>
    </span>
  );

  return (
    // The whole workspace is one card on the gray canvas: toolbar on top, the
    // two panes filling the remaining height.
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-hairline bg-surface shadow-sm">
      {/* Header bar */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-hairline px-5 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-bold text-ink">{t("items.sectionTitle")}</h2>
          {draft.pendingCount > 0 && (
            <Badge variant="warning" className="font-mono">
              {t("editor.pending", { count: draft.pendingCount })}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setFindShowReplace(true);
              setFindOpen(true);
            }}
            title={t("items.findReplaceTip")}
          >
            <Replace />
            {t("items.findReplace")}
          </Button>
          <Button size="sm" variant="outline" onClick={onImport}>
            <Upload />
            {t("items.import")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-status-danger-foreground hover:text-status-danger-foreground"
            disabled={draft.pendingCount + scratchCount === 0 || isSaving}
            onClick={handleDiscardAll}
          >
            <Trash2 />
            {t("editor.discardAll")}
          </Button>
          <Button
            size="sm"
            disabled={draft.pendingCount === 0}
            loading={isSaving}
            onClick={handleSaveAll}
          >
            {t("editor.saveAll", { count: draft.pendingCount })}
          </Button>
        </div>
      </div>

      {/* Two-panel master/detail */}
      <div className="relative flex min-h-0 flex-1 flex-row overflow-hidden">
        {findOpen && (
          <div className="absolute right-4 top-4 z-20">
            <FindReplaceBar
              items={findItems}
              showReplace={findShowReplace}
              onClose={() => setFindOpen(false)}
              onHighlight={() => {}}
              onMatchesChange={() => {}}
              onReplace={(r) => applyReplacements([r])}
              onReplaceAll={(reps) => applyReplacements(reps)}
            />
          </div>
        )}
        {/* Master */}
        <div className="flex shrink-0 flex-col" style={{ width: masterWidth }}>
          <Tabs
            ref={tabsRootRef}
            label={t("items.sectionTitle")}
            value={activeTab}
            onValueChange={(v) => handleTabChange(v as GroupItemKind)}
            className="min-h-0 flex-1 [&>[role=tablist]]:px-3 [&>[role=tabpanel]:not([hidden])]:flex [&>[role=tabpanel]]:min-h-0 [&>[role=tabpanel]]:flex-1 [&>[role=tabpanel]]:flex-col [&>[role=tabpanel]]:pt-0"
            items={[
              {
                value: "dimensions",
                label: tabLabel(t("tabs.dimensions"), counts.dimensions),
                content: activeTab === "dimensions" ? masterPane : null,
              },
              {
                value: "measures",
                label: tabLabel(t("tabs.measures"), counts.measures),
                content: activeTab === "measures" ? masterPane : null,
              },
              {
                value: "variables",
                label: tabLabel(t("tabs.variables"), counts.variables),
                content: activeTab === "variables" ? masterPane : null,
              },
            ]}
          />
        </div>

        <ResizeHandle onPointerDown={startResize} {...resizeProps} />

        {/* Detail */}
        <div
          ref={detailPanelRef}
          className="@container flex min-w-0 flex-1 flex-col overflow-y-auto"
        >
          {sel == null ? (
            <div className="flex flex-1 items-center justify-center">
              <FeedbackState
                state="empty"
                icon={<MousePointerClick />}
                title={t("editor.emptyDetail")}
                description={t("editor.emptyDetailHint")}
                action={
                  <Button variant="outline" size="sm" onClick={handleNew}>
                    <Plus />
                    {t("editor.new" + tabKey)}
                  </Button>
                }
              />
            </div>
          ) : (
            <>
              <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-surface px-5 pb-3 pt-4">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-faint">
                    {eyebrow}
                  </span>
                  {detailBadge && (
                    <Badge variant={detailBadge.variant} className="font-mono">
                      {detailBadge.label}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {selDeleted ? (
                    <Button variant="ghost" size="compact" onClick={handleUndoDelete}>
                      <Undo2 />
                      {t("editor.undoDelete")}
                    </Button>
                  ) : (
                    <>
                      <Button size="compact" disabled={!selDirty} onClick={handleSaveItem}>
                        <Check />
                        {t("editor.saveItem")}
                      </Button>
                      {(selDirty || selStaged || selNew) && (
                        <Button variant="ghost" size="compact" onClick={handleDiscardItem}>
                          <X />
                          {t("editor.discard")}
                        </Button>
                      )}
                      {!selNew && (
                        <Button
                          variant="ghost"
                          size="compact"
                          className="text-status-danger-foreground hover:text-status-danger-foreground"
                          onClick={handleDelete}
                        >
                          <Trash2 />
                          {t("editor.delete")}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className={DETAIL_GRID}>
                {selDeleted ? (
                  <p className="text-[13px] text-faint">{t("editor.deletePending")}</p>
                ) : (
                  <>
                    <div className="min-w-0">
                      {activeTab === "variables" && (
                        <GroupVariableForm
                          value={workingVar(sel)}
                          onChange={(next) => setScratchVar(sel, next)}
                          errors={errorsByKey[sel]}
                        />
                      )}
                      {activeTab === "dimensions" && (
                        <GroupDimensionForm
                          value={workingDim(sel)}
                          onChange={(next) => setScratchDim(sel, next)}
                          errors={errorsByKey[sel]}
                          availableFields={availableFields}
                        />
                      )}
                      {activeTab === "measures" && (
                        <GroupMeasureForm
                          key={sel}
                          value={workingMeasure(sel)}
                          onChange={(next) => setScratchMeasure(sel, next)}
                          errors={errorsByKey[sel]}
                        />
                      )}
                      <GroupItemSyncStatus entries={syncEntries} isNew={selNew} />
                    </div>
                    <ExpressionDecomposition
                      key={`decomposition:${sel}`}
                      graph={expressionGraph}
                      itemId={sel}
                      scope="group"
                      onOpenItem={openItem}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
